// Cross-chat audit tools for agentic chat-log review. Exposed to
// every chat via piChat.js's built-in toolkit; the operator-facing
// surface is the `/audit` slash command which spawns a sub-agent
// with these tools + the Linear MCP for filing findings.
//
// The auditor can see every open chat in the window (not just the
// one it was invoked from) so it can spot regressions across
// sessions, compare tool-description ambiguities across runs, and
// produce sharper proposals than a single-chat view would allow.
//
// Three tools:
//   • list_chats               — chat summaries (no transcript)
//   • read_chat_transcript     — full transcript, UI view / LLM view / both
//   • read_tool_call           — drill into one tool call with lineage
//
// All three accept stable chat_ids returned from list_chats. Tool
// call ids are the same opaque strings the model already sees in
// tool-use blocks.

import { Type } from "@earendil-works/pi-ai";
import { listChatSummaries, getChatById, getAgentById } from "./chatRegistry.js";
import { compactMessages, convertToLlm } from "./piChat.js";

// Rows in analytics_query / execute_python results blow up the
// transcript fast. When `strip_rows` is on (the default), large
// row arrays are replaced with a head sample + count + an elided
// marker so the auditor can see the shape without paying for every
// cell. It can drill into a specific tool_call via read_tool_call
// with `include_rows: true` to get the real rows back.
const ROW_HEAD_SAMPLE = 3;
const STRIP_THRESHOLD = 8;

function stripLargeRows(value) {
    if (value == null) return value;
    if (Array.isArray(value)) {
        if (value.length > STRIP_THRESHOLD && value.every((r) => r && typeof r === "object")) {
            return {
                _elided_rows: true,
                rowCount: value.length,
                head: value.slice(0, ROW_HEAD_SAMPLE).map((r) => stripLargeRows(r)),
            };
        }
        return value.map(stripLargeRows);
    }
    if (typeof value === "object") {
        const out = {};
        for (const [k, v] of Object.entries(value)) {
            // The {values:[...]} shape pi-agent uses for analytics
            // rows — strip the same way as plain dicts.
            if (k === "rows" && Array.isArray(v) && v.length > STRIP_THRESHOLD) {
                out[k] = {
                    _elided_rows: true,
                    rowCount: v.length,
                    head: v.slice(0, ROW_HEAD_SAMPLE),
                };
            } else {
                out[k] = stripLargeRows(v);
            }
        }
        return out;
    }
    return value;
}

function safeClone(value, replacer) {
    try { return JSON.parse(JSON.stringify(value, replacer)); }
    catch { return null; }
}

function makeAuditReplacer() {
    // Ancestor-only cycle detection — see makeReplacer() in
    // exportChatsBundle.js for the full rationale. A grow-only set
    // flags shared-but-acyclic refs (chat snapshots share object
    // identity with the items tree) as "[circular]"; tracking the
    // ancestor chain instead serializes those in full.
    const stack = [];
    return function replacer(_key, value) {
        if (typeof value === "function") return undefined;
        if (typeof value === "object" && value !== null) {
            while (stack.length > 0 && stack[stack.length - 1] !== this) {
                stack.pop();
            }
            if (stack.includes(value)) return "[circular]";
            stack.push(value);
        }
        return value;
    };
}

// Project a chat.items array into a compact tree the auditor can
// reason about. Mirrors the export bundle but with optional row-
// elision so a 10k-row analytics result doesn't single-handedly
// blow the auditor's context.
function projectUiTranscript(items, { stripRows }) {
    const cleaned = safeClone(items ?? [], makeAuditReplacer()) || [];
    return stripRows ? stripLargeRows(cleaned) : cleaned;
}

// Project the pi-agent message array as the LLM sees it after
// convertToLlm + compactMessages. This is "what did the model
// actually have in context on its last turn?" — the most useful
// view for auditing tool-description ambiguity, looping behavior,
// or compaction-driven info loss.
function systemStub(message) {
    const text = typeof message.content === "string"
        ? message.content
        : (message.content || []).map((c) => c?.text || "").join("");
    return {
        role: "system",
        promptChars: text.length,
        ...(message.sections ? { sections: Object.keys(message.sections) } : {}),
        ...(message.toolsAdded?.length ? { toolsAdded: message.toolsAdded.map((t) => t.name) } : {}),
        ...(message.toolsRemoved?.length ? { toolsRemoved: message.toolsRemoved.map((t) => t.name) } : {}),
    };
}

function projectLlmTranscript(messages, { stripRows }) {
    if (!Array.isArray(messages) || !messages.length) return [];
    let working;
    try { working = compactMessages(messages); }
    catch { working = messages; }
    let llmShape;
    try { llmShape = convertToLlm(working); }
    catch { llmShape = working; }
    // The prompt and tool schemas ride in system messages, tens of KB a chat;
    // the audit sees their size and the tool names, not the text.
    llmShape = (Array.isArray(llmShape) ? llmShape : []).map((m) => (m?.role === "system" ? systemStub(m) : m));
    const cleaned = safeClone(llmShape, makeAuditReplacer()) || [];
    return stripRows ? stripLargeRows(cleaned) : cleaned;
}

// ── Tool 1: list_chats ────────────────────────────────────────────

export const listChatsTool = {
    name: "list_chats",
    label: "List chats",
    description:
        "List every chat open in the browser window. Returns compact summaries per chat — chat_id, title, item / turn counts, busy state, first and last user prompts, scope mode. Call this first when auditing across the window; pick one chat_id and drill in with read_chat_transcript / read_tool_call.",
    parameters: Type.Object({}),
    async execute() {
        const summaries = listChatSummaries();
        return {
            content: [{ type: "text", text: JSON.stringify({ chats: summaries }, null, 2) }],
            details: { chats: summaries },
        };
    },
};

// ── Tool 2: read_chat_transcript ──────────────────────────────────

export const readChatTranscriptTool = {
    name: "read_chat_transcript",
    label: "Read chat transcript",
    description:
        "Fetch a chat's full transcript for audit. Two views are available and they DIVERGE in important ways:\n" +
        "  • `ui` — the chat.items tree the human saw, including rendered blocks, sub-agent statuses, kernel suggestions, etc.\n" +
        "  • `llm` — the post-compaction LLM-facing message array (what the model actually had on its most recent turn). This is where you find compaction stubs and synthesized: true staged inputs. Bugs hide in the delta between the two views.\n" +
        "  • `both` — returns { ui, llm }; use when you don't know which one will surface the issue.\n\n" +
        "`strip_rows: true` (default) elides large row payloads (analytics_query results, execute_python row outputs) with a head sample + count, so the auditor's context stays usable across many chats. When you need the actual rows, call read_tool_call on the specific tool_call_id with include_rows: true.",
    parameters: Type.Object({
        chat_id: Type.Unknown({
            description: "Chat id from list_chats. Numbers and string-form numbers both work.",
        }),
        view: Type.Optional(
            Type.String({
                description: "`ui` (default) | `llm` | `both`.",
            }),
        ),
        strip_rows: Type.Optional(
            Type.Boolean({
                description: "Default true. Elide large row arrays to keep context small.",
            }),
        ),
    }),
    async execute(_id, args) {
        const chatId = args?.chat_id;
        const view = (args?.view || "ui").toLowerCase();
        const stripRows = args?.strip_rows !== false;
        const chat = getChatById(chatId);
        if (!chat) {
            return {
                content: [{ type: "text", text: `error: no chat with id ${JSON.stringify(chatId)}. Call list_chats to see available ids.` }],
                isError: true,
            };
        }
        const payload = {
            chat_id: chat.id,
            title: chat.title || "",
            view,
            strip_rows: stripRows,
        };
        if (view === "ui" || view === "both") {
            payload.ui = projectUiTranscript(chat.items, { stripRows });
        }
        if (view === "llm" || view === "both") {
            const agent = getAgentById(chat.id);
            if (!agent) {
                payload.llm = { error: "no live agent for this chat (the chat may be a fork, or the agent failed to initialize)" };
            } else {
                payload.llm = projectLlmTranscript(agent.state?.messages || [], { stripRows });
            }
        }
        return {
            content: [{ type: "text", text: JSON.stringify(payload, null, 2) }],
            details: payload,
        };
    },
};

// ── Tool 3: read_tool_call (with lineage) ─────────────────────────

// Walk chat.items and any nested spawn_subagent / spawn_batch
// children, yielding every tool node found. Yields { path, tool }
// so the auditor knows where in the tree a finding lives.
function* iterAllTools(items, path = []) {
    if (!Array.isArray(items)) return;
    for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (!item || typeof item !== "object") continue;
        const here = [...path, i];
        if (item.kind === "tool") {
            yield { path: here, tool: item };
            // spawn_subagent: child transcript lives on
            // details.messages — flatten its assistant tool calls
            // into the auditable space.
            const detMsgs = Array.isArray(item.details?.messages) ? item.details.messages : null;
            if (detMsgs) {
                for (const t of toolsFromAgentMessages(detMsgs, [...here, "child"])) yield t;
            }
            const detItems = Array.isArray(item.details?.items) ? item.details.items : null;
            if (detItems) {
                for (let j = 0; j < detItems.length; j++) {
                    const child = detItems[j];
                    const childMsgs = Array.isArray(child?.messages) ? child.messages : null;
                    if (childMsgs) {
                        for (const t of toolsFromAgentMessages(childMsgs, [...here, "items", j])) yield t;
                    }
                }
            }
        }
        const children = Array.isArray(item.children) ? item.children : null;
        if (children) {
            for (const t of iterAllTools(children, here)) yield t;
        }
    }
}

// Tool nodes inside a sub-agent's pi-agent message array don't have
// the same shape as chat.items tool nodes — they're toolCall content
// blocks paired with toolResult messages. Flatten them so the
// lineage walker sees one virtual tool per child invocation.
function* toolsFromAgentMessages(messages, path) {
    if (!Array.isArray(messages)) return;
    const resultsById = new Map();
    for (const m of messages) {
        if (m?.role !== "toolResult") continue;
        if (m.toolCallId) resultsById.set(m.toolCallId, m);
    }
    for (const m of messages) {
        if (m?.role !== "assistant") continue;
        const content = Array.isArray(m.content) ? m.content : [];
        for (const c of content) {
            if (c?.type !== "toolCall") continue;
            const result = resultsById.get(c.id) || null;
            yield {
                path,
                tool: {
                    kind: "tool",
                    id: c.id,
                    name: c.name,
                    // pi-ai stores tool-call args under `arguments` in
                    // state.messages; staged blocks use `input`. Reading
                    // only `input` blanked nested-call args (and broke the
                    // from_tool_calls lineage that reads tool.args).
                    args: c.arguments ?? c.input,
                    nested: true,
                    result_text: result?.content?.[0]?.text || null,
                    synthesized: m.synthesized === true || result?.synthesized === true,
                },
            };
        }
    }
}

// Lineage: producers (ids this call READS via from_tool_calls /
// from_tool_call), consumers (later calls that READ this id),
// staged_for (synthetic ids that wrap this id via the `__staged__`
// pattern from subAgentTools.js).
function computeLineage(allTools, focusId) {
    const producers = [];
    const consumers = [];
    const stagedFor = [];

    const focus = allTools.find(({ tool }) => tool.id === focusId);
    if (focus) {
        const ftc = focus.tool.args?.from_tool_calls;
        if (ftc && typeof ftc === "object") {
            for (const [varName, ref] of Object.entries(ftc)) {
                // execute_python uses string ids; spawn_* uses
                // { tool_call_id, slice? } objects.
                if (typeof ref === "string") producers.push({ as: varName, tool_call_id: ref });
                else if (ref && typeof ref === "object" && typeof ref.tool_call_id === "string") {
                    producers.push({ as: varName, tool_call_id: ref.tool_call_id, slice: ref.slice ?? null });
                }
            }
        }
        const ftcSingular = focus.tool.args?.from_tool_call;
        if (typeof ftcSingular === "string") producers.push({ as: "(from_tool_call)", tool_call_id: ftcSingular });
        if (typeof focus.tool.args?.tool_call_id === "string"
            && (focus.tool.name === "expand_preview" || focus.tool.name === "inspect_subagent")) {
            producers.push({ as: `(${focus.tool.name})`, tool_call_id: focus.tool.args.tool_call_id });
        }
    }

    for (const { tool } of allTools) {
        if (tool.id === focusId) continue;
        const ftc = tool.args?.from_tool_calls;
        if (ftc && typeof ftc === "object") {
            for (const [varName, ref] of Object.entries(ftc)) {
                const id = typeof ref === "string" ? ref : ref?.tool_call_id;
                if (id === focusId) {
                    consumers.push({
                        by: tool.id,
                        tool: tool.name,
                        as: varName,
                        slice: typeof ref === "object" ? (ref.slice ?? null) : null,
                    });
                }
            }
        }
        if (tool.args?.from_tool_call === focusId) {
            consumers.push({ by: tool.id, tool: tool.name, as: "(from_tool_call)" });
        }
        if ((tool.name === "expand_preview" || tool.name === "inspect_subagent")
            && tool.args?.tool_call_id === focusId) {
            consumers.push({ by: tool.id, tool: tool.name, as: `(${tool.name})` });
        }
        // Synthetic staged ids look like `<spawnToolCallId>__staged__<var>`.
        if (typeof tool.id === "string" && tool.id.startsWith(`${focusId}__staged__`)) {
            const varName = tool.id.slice(`${focusId}__staged__`.length);
            stagedFor.push({ staged_id: tool.id, var: varName });
        }
    }

    return { producers, consumers, staged_for: stagedFor };
}

export const readToolCallTool = {
    name: "read_tool_call",
    label: "Read tool call",
    description:
        "Fetch the full record for a single tool call inside a chat, plus its data lineage. Returns:\n" +
        "  • args — the input the LLM emitted\n" +
        "  • details — the tool's structured result (analytics rows, python output, sub-agent state, etc.)\n" +
        "  • status / summary / error\n" +
        "  • lineage.producers — tool calls whose data this call READ (via from_tool_calls / from_tool_call / expand_preview / inspect_subagent)\n" +
        "  • lineage.consumers — later tool calls that READ this one's id\n" +
        "  • lineage.staged_for — synthetic staged-input ids that wrap this one (parent → child handoffs from spawn_subagent / spawn_batch)\n\n" +
        "By default rows are returned unstripped — you drilled in deliberately. Pass include_rows: false to keep only the shape.",
    parameters: Type.Object({
        chat_id: Type.Unknown({ description: "Chat id from list_chats." }),
        tool_call_id: Type.String({ description: "The tool_call_id to fetch (e.g. 'toolu_…')." }),
        include_rows: Type.Optional(Type.Boolean({
            description: "Default true. Set false to elide large row payloads in details.",
        })),
    }),
    async execute(_id, args) {
        const chat = getChatById(args?.chat_id);
        if (!chat) {
            return {
                content: [{ type: "text", text: `error: no chat with id ${JSON.stringify(args?.chat_id)}.` }],
                isError: true,
            };
        }
        const focusId = String(args?.tool_call_id || "").trim();
        if (!focusId) {
            return {
                content: [{ type: "text", text: "error: tool_call_id is required." }],
                isError: true,
            };
        }
        const allTools = [...iterAllTools(chat.items || [])];
        const found = allTools.find(({ tool }) => tool.id === focusId);
        if (!found) {
            return {
                content: [{ type: "text", text: `error: no tool call with id ${focusId} in chat ${chat.id}. Use read_chat_transcript to see what's there.` }],
                isError: true,
            };
        }
        const includeRows = args?.include_rows !== false;
        const cleanedTool = safeClone(found.tool, makeAuditReplacer()) || {};
        const finalTool = includeRows ? cleanedTool : stripLargeRows(cleanedTool);
        const lineage = computeLineage(allTools, focusId);
        const payload = {
            chat_id: chat.id,
            tool_call_id: focusId,
            location: found.path,
            tool: finalTool,
            lineage,
        };
        return {
            content: [{ type: "text", text: JSON.stringify(payload, null, 2) }],
            details: payload,
        };
    },
};

export const AUDIT_TOOLS = [listChatsTool, readChatTranscriptTool, readToolCallTool];
